import React, { FC, useEffect, useRef } from 'react';
import { StyleSheet, View, TouchableOpacity, StyleProp, ViewStyle } from 'react-native';

import colors from '@packages/core/styles/colors';
import { Icon } from './icon';

interface AlertBarProps {
    show: boolean;
    style?: StyleProp<ViewStyle>;
    success?: boolean;
    error?: boolean;
    iconHide?: boolean;
    warning?: boolean;
    redWarning?: boolean;
    info?: boolean;
    /** Auto-dismiss after this many milliseconds unless the user closes it first. */
    autoHideMs?: number;
    onClose(): void;
    children?: React.ReactNode;
}

export const AlertBar: FC<AlertBarProps> = ({
    show,
    style,
    success,
    error,
    warning,
    redWarning,
    info,
    autoHideMs,
    onClose,
    children,
    iconHide = false,
}) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const autoHideTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearAutoHideTimeout = () => {
        if (autoHideTimeoutIdRef.current == null) {
            return;
        }
        clearTimeout(autoHideTimeoutIdRef.current);
        autoHideTimeoutIdRef.current = null;
    };

    useEffect(() => {
        clearAutoHideTimeout();
        if (!show || autoHideMs == null) {
            return undefined;
        }
        autoHideTimeoutIdRef.current = setTimeout(() => {
            autoHideTimeoutIdRef.current = null;
            onCloseRef.current();
        }, autoHideMs);
        return () => {
            clearAutoHideTimeout();
        };
    }, [show, autoHideMs]);

    function handleCloseButtonPress() {
        clearAutoHideTimeout();
        onClose();
    }

    if (!show) {
        return null;
    }

    return (
        <View
            style={[
                styles.alert,
                info && { backgroundColor: colors.blueFour },
                success && { backgroundColor: colors.greenFour },
                error && { backgroundColor: colors.redFour },
                warning && { backgroundColor: colors.yellowFour },
                redWarning && { backgroundColor: colors.redFour },
                style,
            ]}
        >
            <View
                style={[
                    styles.iconWrapper,
                    success && { backgroundColor: colors.greenOne },
                    error && { backgroundColor: colors.redOne },
                ]}
            >
                {success && <Icon name="checkmark" color="white" size={16} />}
                {error && <Icon name="x-close" color="white" size={16} />}
                {warning && <Icon name="warning" color="yellowOne" size={24} />}
                {redWarning && <Icon name="warning" color="redOne" size={24} />}
                {info && <Icon name="info-solid" color="blueOne" size={24} />}
            </View>

            <View style={styles.childrenOuter}>{children}</View>

            {iconHide === false && (
                <TouchableOpacity onPress={handleCloseButtonPress} style={styles.closeButton}>
                    <Icon name="x-close" />
                </TouchableOpacity>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    alert: {
        padding: 16,
        paddingRight: 50,
        position: 'relative',
        flexDirection: 'row',
        backgroundColor: colors.grayOne,
    },
    iconWrapper: {
        height: 24,
        width: 24,
        borderRadius: 100,
        justifyContent: 'center',
        alignItems: 'center',
    },
    childrenOuter: {
        flex: 1,
        paddingTop: 2,
        paddingLeft: 16,
    },
    closeButton: {
        top: 6,
        right: 6,
        width: 44,
        height: 44,
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export const alertBarDefaultStyles = StyleSheet.create({
    text: {
        fontSize: 17,
        lineHeight: 20,
        fontWeight: '600',
    },
    link: {
        fontSize: 15,
        lineHeight: 20,
    },
});
